import { Component, computed, ElementRef, inject, Input, signal, ViewChild, WritableSignal } from '@angular/core';
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  AbstractControl,
  AsyncValidatorFn,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors, ValidatorFn,
  Validators
} from "@angular/forms";
import { ApiService } from "../../api/services/api.service";
import { WalletWithRentalDto } from "../../api/models/fraud.models";
import { catchError, map, merge, Observable, of, switchMap, take } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router, RouterLink } from "@angular/router";
import { NgClass } from "@angular/common";
import { contactAsBot } from "../../partials/variables";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatRipple } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { AuthService } from '../../partials/services/auth.service';
import { ProjectSettingsService } from '../../partials/services/project-settings.service';

export const addressValidator = (_apiService: ApiService, translateService: TranslateService): AsyncValidatorFn => (control: AbstractControl): Observable<ValidationErrors | null> => {
  return of(control.value).pipe(switchMap((address) => {
    if (address.length < 34) {
      let message = translateService.instant('common.errors.error3');
      return of({ calculateRentalPlanForWalletError: message })
    }
    const calculateRentalPlanForWalletRequest = _apiService.validWallet(address).pipe(
      map(() => {
        return null;
      }),
      catchError(({ error }) => {
        let message = translateService.instant('common.errors.error5');
        switch (error.code) {
          case 1: {
            message = translateService.instant('common.errors.error6', { address });
            break;
          }
          case 3: {
            message = translateService.instant('common.errors.error15');
            break;
          }
          case 4: {
            message = translateService.instant('common.errors.error14');
            break;
          }
          case 10: {
            message = translateService.instant('common.errors.error21');
            break;
          }
        }
        return of({ calculateRentalPlanForWalletError: message })
      })
    );
    return address ? calculateRentalPlanForWalletRequest : of(null);
  }));
};

export function tronAddressValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Проверка на пустоту поля
    if (!value || !(value.length >= 34)) {
      return null;
    }

    // Простейшая проверка формата (начинается с "T" и длина 34 символа)
    const isValidFormat = /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(value);

    if (!isValidFormat) {
      return { invalidTronAddress: true };
    }

    return null;
  };
}

@Component({
    selector: 'app-add-wallet',
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatIcon,
        MatRipple,
        NgClass,
        RouterLink
    ],
    templateUrl: './add-wallet.component.html',
    styleUrl: './add-wallet.component.scss',
    host: {
        class: 'py-4 flex flex-col'
    }
})
export default class AddWalletComponent {
  @ViewChild('addressInput') addressInput!: ElementRef;

  @Input({ required: true })
  public readonly subscribedWallets: WalletWithRentalDto[] = [];

  public addressErrorMessage: WritableSignal<string> = signal('');

  private readonly translateService: TranslateService = inject(TranslateService);

  private readonly _fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);

  private readonly _apiService: ApiService = inject(ApiService);

  readonly authService: AuthService = inject(AuthService);

  private readonly _projectSettingsService: ProjectSettingsService = inject(ProjectSettingsService);

  readonly isAmlEnergyActive = computed(() => this._projectSettingsService.settings()?.isAmlEnergyActive ?? false);

  public readonly form = this._fb.group({
    address: this._fb.control('', [
      Validators.required,
      tronAddressValidator(),
      (control): ValidationErrors | null => {
        const existingWallet = this.subscribedWallets.find(
          ({ wallet: { address } }) => address === control.value
        );

        if (existingWallet) {
          switch (existingWallet.wallet.walletType) {
            case 'USER_PLAN':
              return {
                walletWasAddedToMonitoring: true,
              }
            case 'AML_ENERGY':
              return {
                walletWasAdded: true,
              }
          }
        }
        return null;
      }
    ],
      [addressValidator(this._apiService, this.translateService)]
    ),
    currency: this._fb.control('USDT', [Validators.required]),
    name: this._fb.control(''),
    network: this._fb.control('TRX', [Validators.required]),
  });

  private readonly _router: Router = inject(Router);

  private readonly _matDialog: MatDialog = inject(MatDialog);

  currentLang = this.translateService.currentLang;

  constructor() {
    const { controls: { address } } = this.form;
    const { statusChanges, valueChanges } = address;
    merge(statusChanges, valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateAddressErrorMessage();
      });

    // TODO пока что сказали скрыть
    // Вызов диалога для кошельков которые не явщяются TRC-20 адресами
    // statusChanges.pipe(debounceTime(2000), takeUntilDestroyed())
    //   .subscribe(() => {
    //     if (address.hasError('invalidTronAddress')) {
    //       this.openBalanceDialog();
    //     }
    //   });
  }

  // openBalanceDialog(): void {
  //   const config: MatDialogConfig = {
  //     width: '100vw',
  //     maxWidth: '100vw',
  //     height: '100dvh',
  //     maxHeight: '100dvh',
  //     panelClass: 'dialog-balance'
  //   };
  //   this._matDialog.open(DialogBalanceComponent, config);
  // }

  updateAddressErrorMessage(): void {
    const { controls: { address } } = this.form;
    let message = '';
    switch (true) {
      case address.hasError('required'): {
        message = 'common.errors.error2';
        break;
      }
      case (address.hasError('invalidTronAddress')): {
        message = 'common.errors.error3';
        break;
      }
      case address.hasError('calculateRentalPlanForWalletError'): {
        message = address.errors?.['calculateRentalPlanForWalletError'];
        break;
      }
      case address.hasError('apiError'): {
        message = address.errors?.['apiError'];
        break;
      }
      case address.hasError('walletWasAdded'): {
        message = 'common.errors.error4';
        break;
      }
      case address.hasError('walletWasAddedToMonitoring'): {
        message = 'common.errors.error41';
        break;
      }
    }

    this.addressErrorMessage.set(message);
  }

  submit() {
    if (this.form.valid) {
      const body = this.form.getRawValue();
      const user = this.authService.accountDto();
      this._apiService.addWallet(body).pipe(
        take(1),
        switchMap(() => {
          return !user?.hasWallets
            ? this._apiService.reportScanWallet({ wallet: body.address })
            : of(null);
        })
      ).subscribe({
        next: () => {
          this.authService.refresh();
          this._router.navigate(['/subscribed-wallets']);
        },
        error: ({ error }) => this._handleAddWalletError(error)
      });
    }
  }

  private _handleAddWalletError(error: any): void {
    let message = this.translateService.instant('common.errors.error5');
    switch (error.code) {
      case 1:
        message = this.translateService.instant('common.errors.error24');
        break;
      case 3:
        message = this.translateService.instant('common.errors.error10', { contact: contactAsBot });
        break;
      case 5:
        message = this.translateService.instant('common.errors.error4');
        break;
      case 10:
        message = this.translateService.instant('common.errors.error21');
        break;
      case 11:
        message = this.translateService.instant('common.errors.error22');
        break;
      case 18:
        message = this.translateService.instant('common.errors.error23');
        break;
    }
    this.form.controls.address.setErrors({ apiError: message });
  }

  enableForm(): void {
    this.form.enable({ emitEvent: false });
    this.form.controls.address.reset();
    this._focusAddressInput();
  }

  private _focusAddressInput(): void {
    setTimeout(() => {
      if (this.addressInput) {
        this.addressInput?.nativeElement.focus();
      }
    }, 50);
  }
}
